import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, PackageCheck, Receipt, Truck, Undo2 } from 'lucide-react';
import {
  ALLOWED_WORKFLOW_TRANSITIONS,
  PERMISSIONS,
  type SalesLineWorkflowStatus,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useSalesOrderQuery } from '../api/sales.api';
import {
  useAdvanceWorkflowMutation,
  useDeliverOrderMutation,
  useIssueLineStockMutation,
  useOpticalJobsQuery,
  usePrescriptionsQuery,
} from '../api/optical.api';
import { PrescriptionForm } from '../components/PrescriptionForm';
import { ReturnDialog } from '../components/ReturnDialog';
import { useOrderOpticalJobsQuery } from '@/modules/optical-orders/api/opticalOrders.api';
import { JobStatusBadge } from '@/modules/optical-orders/components/JobStatusBadge';

/**
 * One optical order, from the counter to collection.
 *
 * The two quantities are shown separately throughout, because they answer different questions: what
 * has left the shelf, and what the customer actually has. A frame at the lab shows the first
 * without the second, and that gap is the thing the staff need to see.
 */
export function OpticalOrderPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const { orderId = '' } = useParams<{ orderId: string }>();

  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [returnOpen, setReturnOpen] = useState(false);

  const order = useSalesOrderQuery(orderId);
  const jobs = useOpticalJobsQuery(orderId);
  const canTrack = can(PERMISSIONS.OPTICAL_VIEW);
  const trackedJobs = useOrderOpticalJobsQuery(canTrack ? orderId : undefined);
  const prescriptions = usePrescriptionsQuery(orderId);

  const advance = useAdvanceWorkflowMutation(orderId);
  const issue = useIssueLineStockMutation(orderId);
  const deliver = useDeliverOrderMutation(orderId);

  const canAct = can(PERMISSIONS.SALES_CREATE);
  const canReturn = can(PERMISSIONS.SALES_REFUND);

  if (order.isPending) return <LoadingState />;
  if (order.isError || !order.data) {
    return (
      <Alert tone="error">{apiErrorMessage(order.error) ?? t('sales.errors.loadOrders')}</Alert>
    );
  }

  const data = order.data;
  const deferredLines = data.lines.filter((line) => line.fulfilmentMode !== 'IMMEDIATE');
  // A pair taken as an optical job moves only through its own tracking screen; the line workflow here serves the rest, so no line is ever moved by both.
  const checkoutJobs = (trackedJobs.data ?? []).filter((job) => job.origin === 'CHECKOUT');
  const jobLineIds = new Set(
    canTrack
      ? checkoutJobs.flatMap((job) => job.lineIds)
      : data.lines.filter((line) => line.opticalJobId !== null).map((line) => line.id),
  );
  const legacyDeferred = deferredLines.filter((line) => !jobLineIds.has(line.id));
  const hasDelivered = data.lines.some((line) => line.quantityDelivered > 0);
  const currentStatus = jobs.data?.[0]?.workflowStatus ?? 'NOT_STARTED';
  const nextStates = ALLOWED_WORKFLOW_TRANSITIONS[currentStatus] ?? [];

  const fail = (caught: unknown, fallback: string) => {
    setFeedback(null);
    setError(apiErrorMessage(caught) ?? fallback);
  };
  const succeed = (message: string) => {
    setError(null);
    setFeedback(message);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.reference}
        subtitle={[data.customerName, data.branchName].filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                navigate(deferredLines.length > 0 ? '/sales/optical-jobs' : '/sales/orders')
              }
            >
              <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />
              {deferredLines.length > 0 ? t('opticalOrders.queue.title') : t('sales.orders.title')}
            </Button>
            {/* Reprints this order's receipt in the same small window the counter opens after a sale; the print page itself reads the order again. */}
            <Button
              variant="secondary"
              onClick={() =>
                window.open(`/print/receipt/${data.id}`, '_blank', 'noopener,width=420,height=640')
              }
            >
              <Receipt className="size-4" aria-hidden />
              {t('sales.print.printReceipt')}
            </Button>
            {canReturn && hasDelivered ? (
              <Button variant="secondary" onClick={() => setReturnOpen(true)}>
                <Undo2 className="size-4" aria-hidden />
                {t('sales.returns.take')}
              </Button>
            ) : null}
          </div>
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={data.status === 'COMPLETED' ? 'success' : 'brand'}>
          {t(`sales.statuses.${data.status}`)}
        </Badge>
        {/* A cancelled order owes nothing and has no payment status, so none is shown — never "Unpaid". */}
        {data.status === 'CANCELLED' ? null : (
          <Badge tone={data.paymentStatus === 'PAID' ? 'success' : 'warning'}>
            {t(`sales.paymentStatuses.${data.paymentStatus}`)}
          </Badge>
        )}
        <span className="text-muted text-xs">
          {t(`sales.fulfilmentStatuses.${data.fulfilmentStatus}`)}
        </span>
        <span className="text-ink ms-auto font-medium" dir="ltr">
          {format(data.totalMinor)}
          {data.status !== 'CANCELLED' && data.balanceMinor > 0 ? (
            <span className="ms-2 text-amber-700">
              {t('sales.optical.owing', { amount: format(data.balanceMinor) })}
            </span>
          ) : null}
        </span>
      </div>

      {!canTrack && data.lines.some((line) => line.opticalJobId !== null) ? (
        <Alert tone="info">{t('opticalOrders.orderPage.needsOpticalView')}</Alert>
      ) : null}

      {checkoutJobs.length > 0 ? (
        <Card>
          <CardHeader
            title={t('opticalOrders.orderPage.jobsTitle')}
            subtitle={t('opticalOrders.orderPage.jobsHint')}
          />
          <ul className="divide-line divide-y">
            {checkoutJobs.map((job) => (
              <li key={job.id} className="relative flex flex-wrap items-center gap-3 px-5 py-3">
                <span className="text-ink font-mono text-sm font-semibold" dir="ltr">
                  {job.reference}
                </span>
                <JobStatusBadge status={job.status} />
                <Link
                  to={`/sales/optical-jobs/${job.id}`}
                  aria-label={t('opticalOrders.tracking.viewJob', { reference: job.reference })}
                  className="text-brand-700 ms-auto inline-flex items-center gap-1 text-sm font-medium after:absolute after:inset-0 after:content-['']"
                >
                  {t('opticalOrders.orderPage.track')}
                  <ChevronRight className="size-4 rtl:rotate-180" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {legacyDeferred.length > 0 ? (
        <Card>
          <CardHeader
            title={t('sales.optical.job')}
            subtitle={t(`sales.workflow.${currentStatus}`)}
            action={
              canAct && nextStates.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {nextStates.map((status) => (
                    <Button
                      key={status}
                      variant="secondary"
                      size="sm"
                      isLoading={advance.isPending}
                      onClick={() =>
                        advance.mutate(status as SalesLineWorkflowStatus, {
                          onSuccess: () => succeed(t(`sales.workflow.${status}`)),
                          onError: (caught) => fail(caught, t('sales.optical.workflowError')),
                        })
                      }
                    >
                      {t(`sales.workflow.${status}`)}
                    </Button>
                  ))}
                </div>
              ) : undefined
            }
          />

          <ol className="flex flex-wrap gap-2 p-4 pt-0">
            {Object.keys(ALLOWED_WORKFLOW_TRANSITIONS).map((status) => (
              <li key={status}>
                <span
                  className={
                    status === currentStatus
                      ? 'bg-brand-soft text-brand rounded px-2 py-1 text-xs font-medium'
                      : 'text-muted rounded px-2 py-1 text-xs'
                  }
                >
                  {t(`sales.workflow.${status}`)}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      <Card>
        <CardHeader title={t('sales.optical.lines')} subtitle={t('sales.optical.linesHint')} />
        <ul className="divide-line divide-y">
          {data.lines.map((line) => {
            const issuable = line.quantityOrdered - line.quantityStockIssued;
            const deliverable = line.quantityStockIssued - line.quantityDelivered;

            return (
              <li key={line.id} className="flex flex-wrap items-center gap-3 p-4">
                <span className="min-w-0 flex-1">
                  <span className="text-ink block truncate font-medium">{line.productName}</span>
                  <span className="text-muted font-mono text-xs" dir="ltr">
                    {line.sku}
                  </span>
                </span>

                <span className="text-muted flex flex-col text-xs" dir="ltr">
                  <span>{t('sales.optical.ordered', { count: line.quantityOrdered })}</span>
                  <span>{t('sales.optical.issued', { count: line.quantityStockIssued })}</span>
                  <span>{t('sales.optical.delivered', { count: line.quantityDelivered })}</span>
                </span>

                {canAct && !jobLineIds.has(line.id) ? (
                  <span className="flex flex-wrap gap-2">
                    {issuable > 0 ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        isLoading={issue.isPending}
                        onClick={() =>
                          issue.mutate(
                            { lineId: line.id, quantity: issuable },
                            {
                              onSuccess: () => succeed(t('sales.optical.issuedOk')),
                              onError: (caught) => fail(caught, t('sales.optical.issueError')),
                            },
                          )
                        }
                      >
                        <Truck className="size-4" aria-hidden />
                        {t('sales.optical.issue')}
                      </Button>
                    ) : null}

                    {deliverable > 0 ? (
                      <Button
                        size="sm"
                        isLoading={deliver.isPending}
                        onClick={() =>
                          deliver.mutate([{ lineId: line.id, quantity: deliverable }], {
                            onSuccess: () => succeed(t('sales.optical.deliveredOk')),
                            onError: (caught) => fail(caught, t('sales.optical.deliverError')),
                          })
                        }
                      >
                        <PackageCheck className="size-4" aria-hidden />
                        {t('sales.optical.deliver')}
                      </Button>
                    ) : null}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </Card>

      {data.customerId && legacyDeferred.length > 0 ? (
        <PrescriptionForm
          orderId={orderId}
          customerId={data.customerId}
          existing={prescriptions.data?.[0]}
          onSaved={succeed}
        />
      ) : null}

      <ReturnDialog
        orderId={orderId}
        orderReference={data.reference}
        isOpen={returnOpen}
        onClose={() => setReturnOpen(false)}
        onCreated={(reference) => succeed(t('sales.returns.createdOk', { reference }))}
      />
    </div>
  );
}
